import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { getPublicSettings } from "../../api/clientPortalPublic";
import PortalServiceHelperModal from "../../components/clientPortal/PortalServiceHelperModal";
import {
  ClientPortalAuthProvider,
  useClientPortalAuth,
} from "../../context/ClientPortalAuthContext";
import { formatApiError } from "../../utils/formatApiError";
import { resolveAssetUrl } from "../../utils/assetUrl";
import { resolvePortalBrandColors } from "../../utils/portalBranding";
import "../OnlineBookingSectionCompact.css";

const buildSafeBackground = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const safe = encodeURI(resolveAssetUrl(trimmed)).replace(/"/g, "%22");
  return safe;
};

const PortalShell = ({ title, subtitle, children }) => (
  <div className="onlineBookingShell">
    <div className="onlineBookingWide online-booking-wide online-booking-compact text-base md:text-lg">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="bg-rose-500 px-6 py-4">
          <h1 className="text-xl md:text-2xl font-semibold text-white">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm md:text-base text-rose-100">{subtitle}</p>
          )}
        </div>
        <div className="px-6 py-8">{children}</div>
      </div>
    </div>
  </div>
);

const ACTIVATION_BANNER_TITLE = "Your account is almost ready!";
const ACTIVATION_BANNER_BODY =
  "You can book appointments now. Rewards and Gift Cards will appear once the salon activates your access. If it's not activated within 48 hours, please contact the salon or ask at your appointment.";

function ClientPortalContent({ salonId, settings, brandName, logoUrl, error }) {
  const { token, client, clientMe, clientLogout } = useClientPortalAuth();
  const navigate = useNavigate();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [serviceHelperOpen, setServiceHelperOpen] = useState(false);
  const [assistantHandlers, setAssistantHandlers] = useState(null);
  const homePath = salonId ? `/portal/salon/${salonId}` : "/login";

  const resolveFirstName = (profile) => {
    const firstName = (profile?.first_name || "").trim();
    if (firstName) return firstName;
    const fullName = (profile?.full_name || profile?.name || "").trim();
    if (!fullName) return null;
    return fullName.split(/\s+/)[0];
  };

  const greetingName = resolveFirstName(client);

  const handleSignOut = () => {
    clientLogout();
    navigate(homePath, { replace: true });
  };

  useEffect(() => {
    if (!token) {
      setBannerDismissed(false);
      return;
    }
    let active = true;
    const loadClient = async () => {
      try {
        await clientMe();
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clientLogout();
        }
      }
    };
    loadClient();
    return () => {
      active = false;
    };
  }, [token, clientMe, clientLogout]);

  const showActivationBanner =
    token &&
    client &&
    client.portal_is_activated === false &&
    !bannerDismissed;

  const portalAiEnabled = Boolean(settings?.portal_ai_enabled);
  const canOpenServiceHelper = portalAiEnabled && Boolean(token);
  const brandColors = useMemo(
    () => resolvePortalBrandColors(settings || {}),
    [settings]
  );
  const portalThemeStyle = {
    "--portal-primary-color": brandColors.primary,
    "--portal-primary-text-color": brandColors.primaryText,
    "--portal-nav-color": brandColors.nav,
    "--portal-nav-text-color": brandColors.navText,
    "--portal-nav-active-text-color": brandColors.navActiveText,
  };
  const navButtonClass =
    "rounded-full px-4 py-2 text-sm font-semibold transition";
  const getNavLinkStyle = (isActive) =>
    isActive
      ? {
          backgroundColor: "#ffffff",
          color: brandColors.navActiveText,
        }
      : {
          backgroundColor: "rgba(255,255,255,0.12)",
          color: brandColors.navText,
        };

  return (
    <div className="onlineBookingShell clientPortalTheme" style={portalThemeStyle}>
      <div className="onlineBookingWide online-booking-wide online-booking-compact text-base md:text-lg">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div
            className="portalNavBar relative z-40 px-6 py-4 pointer-events-auto"
            style={{
              backgroundColor: brandColors.nav,
              color: brandColors.navText,
            }}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <div className="h-12 w-12 rounded-full bg-white/10 border border-white/30 flex items-center justify-center overflow-hidden">
                    <img
                      src={logoUrl}
                      alt={brandName}
                      className="h-full w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-white font-semibold">
                    {brandName.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <h1 className="text-xl md:text-2xl font-semibold">
                    {brandName}
                  </h1>
                  <p className="text-sm md:text-base opacity-85">
                    Online Booking Portal
                  </p>
                </div>
              </div>
              <nav className="relative z-40 flex flex-wrap items-center gap-2 pointer-events-auto">
                <NavLink
                  to={`/portal/salon/${salonId}/book`}
                  className={navButtonClass}
                  style={({ isActive }) => getNavLinkStyle(isActive)}
                >
                  Book
                </NavLink>
                <NavLink
                  to={`/portal/salon/${salonId}/appointments`}
                  className={navButtonClass}
                  style={({ isActive }) => getNavLinkStyle(isActive)}
                >
                  My Appointments
                </NavLink>
                <NavLink
                  to={`/portal/salon/${salonId}/profile`}
                  className={navButtonClass}
                  style={({ isActive }) => getNavLinkStyle(isActive)}
                >
                  Profile
                </NavLink>
                <NavLink
                  to={`/portal/salon/${salonId}/gift-cards`}
                  className={navButtonClass}
                  style={({ isActive }) => getNavLinkStyle(isActive)}
                >
                  Gift Cards
                </NavLink>
                <NavLink
                  to={`/portal/salon/${salonId}/rewards`}
                  className={navButtonClass}
                  style={({ isActive }) => getNavLinkStyle(isActive)}
                >
                  Rewards
                </NavLink>
                <button
                  type="button"
                  onClick={() => setServiceHelperOpen(true)}
                  aria-disabled={!canOpenServiceHelper}
                  title={
                    !portalAiEnabled
                      ? "This helper is not enabled for this salon."
                      : !token
                      ? "Sign in to use the service helper."
                      : ""
                  }
                  className={navButtonClass}
                  style={
                    canOpenServiceHelper
                      ? {
                          backgroundColor: "#ffffff",
                          color: brandColors.navActiveText,
                        }
                      : {
                          backgroundColor: "rgba(255,255,255,0.10)",
                          color: `${brandColors.navText}99`,
                        }
                  }
                >
                  Help me choose
                </button>
                {token ? (
                  <>
                    <span className="hidden sm:inline-flex items-center px-2 text-xs md:text-sm text-white/80">
                      Hi, {greetingName || "there"}
                    </span>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className={navButtonClass}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: brandColors.navText,
                      }}
                    >
                      Sign out
                    </button>
                  </>
                ) : null}
              </nav>
            </div>
          </div>

          <div className="px-6 py-6 space-y-4">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}
            {showActivationBanner && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm md:text-base">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{ACTIVATION_BANNER_TITLE}</div>
                    <div className="mt-1">{ACTIVATION_BANNER_BODY}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBannerDismissed(true)}
                    className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <Outlet
              context={{
                settings,
                salonId,
                setAssistantHandlers,
              }}
            />
          </div>
        </div>
      </div>
      <PortalServiceHelperModal
        open={serviceHelperOpen}
        onClose={() => setServiceHelperOpen(false)}
        salonId={salonId}
        token={token}
        settings={settings}
        assistantHandlers={assistantHandlers}
      />
    </div>
  );
}

export default function ClientPortalLayout() {
  const { salonId } = useParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalUnavailable, setPortalUnavailable] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      if (!salonId) return;
      setLoading(true);
      setError("");
      setPortalUnavailable(false);
      try {
        const data = await getPublicSettings(salonId);
        if (!active) return;
        setSettings(data || null);
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 404) {
          setPortalUnavailable(true);
          setSettings(null);
        } else {
          setError(formatApiError(err));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSettings();
    return () => {
      active = false;
    };
  }, [salonId]);

  const brandName = useMemo(() => {
    const name = settings?.brand_name;
    if (name && String(name).trim()) return String(name).trim();
    return "Client Portal";
  }, [settings?.brand_name]);

  const logoUrl = buildSafeBackground(settings?.logo_url || "");

  if (loading) {
    return (
      <PortalShell title="Client Portal" subtitle="Loading portal settings...">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
          Loading...
        </div>
      </PortalShell>
    );
  }

  if (portalUnavailable) {
    return (
      <PortalShell title="Client Portal">
        <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Online booking is not available yet.
          </h2>
          <p className="mt-2 text-sm md:text-base text-slate-600">
            Please contact the salon for assistance.
          </p>
        </div>
      </PortalShell>
    );
  }

  if (error && !settings) {
    return (
      <PortalShell title="Client Portal">
        <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700">
          {error}
        </div>
      </PortalShell>
    );
  }

  return (
    <ClientPortalAuthProvider salonId={salonId}>
      <ClientPortalContent
        salonId={salonId}
        settings={settings}
        brandName={brandName}
        logoUrl={logoUrl}
        error={error}
      />
    </ClientPortalAuthProvider>
  );
}
